<template>
  <div :class="{ 'airport-handlers': true, 'is-loading': !loaded }">
    <fieldset>
      <legend>Handlers</legend>

      <d-alert
        v-if="referenceId === 0"
        theme="light"
        show
        class="text-center">
        You will be able to see and add airport handlers after saving this customer.
      </d-alert>

      <d-alert
        v-if="loadError"
        theme="warning"
        show
        class="text-center">
        An error occurred during loading airport handlers.
      </d-alert>

      <d-row
        v-if="loaded && !loadError && referenceId !== 0"
        class="pr-3 pl-3 mb-3">
        <validation-observer ref="airport-handler-form" tag="form" style="width: calc(100% - 100px); display: flex;">
          <div class="w-50">
            <validation-provider
              rules="required"
              name="airport_id"
              v-slot="{ errors }">
              <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                <v-select
                  :options="filteredListOfAirports"
                  v-model="airport_id"
                  :reduce="airport => airport.id"
                  :filterBy="filterByAirports"
                  placeholder="Select an airport"
                  :maxItemsDisplay="100"
                  label="id">
                  <template slot="no-options">
                    There are no airports for the provided phrase&hellip;
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="d-center">
                      {{ option.icao }} - ({{ option.name }})
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center">
                      {{ option.icao }} - ({{ option.name }})
                    </div>
                  </template>
                </v-select>
              </div>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="w-50 ml-2">
            <validation-provider
              rules="required"
              name="handler_id"
              v-slot="{ errors }">
              <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                <v-select
                  :disabled="airport_id === null"
                  :options="handlers"
                  v-model="handler_id"
                  :reduce="handler => handler.id"
                  :filterBy="filterByHandlers"
                  placeholder="Select an handler"
                  :maxItemsDisplay="100"
                  label="name">
                  <template slot="no-options">
                    <template>There are no handlers for the provided phrase&hellip;</template>
                  </template>
                  <template slot="option" slot-scope="option">
                    <div class="d-center">
                      {{ option.name }}
                    </div>
                  </template>
                  <template slot="selected-option" slot-scope="option">
                    <div class="selected d-center">
                      {{ option.name }}
                    </div>
                  </template>
                </v-select>
              </div>
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>
        </validation-observer>


        <d-button
          theme="success"
          @click.prevent="addAirportHandler"
          outline
          class="ml-auto"
          style="width: 80px;">
          Add
        </d-button>
      </d-row>

      <d-row
        v-if="loaded && !loadError && referenceId !== 0 && airportHandlers.length !== 0"
        class="pl-3 pr-3">
        <table class="table">
          <thead>
            <tr>
              <th>Airport</th>
              <th>Handler</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
           <tr
            v-for="(airportHandler, index) of airportHandlers"
            :key="'airport-handler-' + index">
            <td class="align-middle" style="width: 50%;">
              {{ airportHandler.airport_name }}
            </td>
            <td class="align-middle" style="width: 50%;">
              {{ airportHandler.handler_name }}
            </td>
            <td class="align-middle" style="width: 60px;">
              <d-button
                theme="danger"
                :disabled="removeInProgress"
                @click.prevent="removeAirportHandler(airportHandler.airport_id, airportHandler.handler_id)"
                title="Delete">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
           </tr>
          </tbody>
        </table>
      </d-row>

      <d-alert
        v-if="loaded && !loadError && referenceId !== 0 && airportHandlers.length === 0"
        theme="light"
        show
        class="text-center">
        There are no airport handlers for this customer.
      </d-alert>
    </fieldset>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../../utils/FormUtils.js';
import ListUtils from './../../utils/ListUtils.js';

export default {
  name: 'airport-handlers-list',
  props: {
    referenceType: {
      type: String,
      required: true
    },
    referenceId: {
      type: Number,
      required: true
    },
    onlyOneHandlerPerAirport: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    addedAirports () {
      return this.airportHandlers.map(xref => xref.airport_id);
    },
    filteredListOfAirports () {
      if (this.onlyOneHandlerPerAirport) {
        return this.$root.enabledAirports.filter(airport => this.addedAirports.indexOf(airport.id) === -1);
      }

      return this.$root.enabledAirports;
    }
  },
  data () {
    return {
      loaded: false,
      loadError: false,
      handlers: [],
      loadedHandlers: false,
      airport_id: null,
      handler_id: null,
      airportHandlers: [],
      removeInProgress: false
    };
  },
  watch: {
    airport_id (newValue) {
      if (newValue !== null) {
        this.handler_id = null;
        this.$refs['airport-handler-form'].reset();
        this.debouncedFetchHandlers();
      }
    }
  },
  mounted () {
    if (this.referenceId === 0) {
      this.loaded = true;
    } else {
      this.loadAirportHandlers();
    }

    this.$bus.$on('airport-handlers-list-reload', this.reload);
  },
  methods: {
    loadAirportHandlers () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/customers/get-airport-handler/' + parseInt(this.referenceId, 10),
        loadedKey: 'loaded',
        outputKey: 'airportHandlers',
        errorKey: 'loadError'
      });
    },
    filterByAirports (option, label, search) {
      return (option.name + ' ' + option.icao).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    filterByHandlers (option, label, search) {
      return (option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    // eslint-disable-next-line
    debouncedFetchHandlers: debounce(function () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/handlers/items',
        outputKey: 'handlers',
        loadedKey: 'loadedHandlers',
        params: {
          where: {
            airport_id: this.airport_id
          }
        },
        errorKey: 'loadError'
      });
    }, 500),
    addAirportHandler () {
      FormUtils.validate(this.$refs['airport-handler-form'], this.submitForm);
    },
    submitForm () {
      FormUtils.submit({
        $http: this.$http,
        endpoint: '/customers/add-airport-handler/' + parseInt(this.referenceId, 10),
        formData: {
          airport_id: this.airport_id,
          handler_id: this.handler_id
        },
        successTitle: 'Handler added',
        successText: '',
        errorTitle: 'An error occurred',
        errorText: 'The system was unable to save the handler for this airport. Please try again.',
        successAction: () => {
          this.loading = false;
          this.isVisible = false;
          this.airport_id = null;
          this.$bus.$emit('airport-handlers-list-reload');
        }
      });
    },
    removeAirportHandler (airportId, handlerId) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/customers/delete-airport-handler/' + parseInt(this.referenceId, 10),
        params: {
          airport_id: airportId,
          handler_id: handlerId
        },
        successTitle: 'Agent\'s Airport removed',
        successText: 'The selected agent\'s airport has been removed',
        confirmTitle: 'Do you really want to remove this agent\'s airport?',
        confirmText: 'This operation cannot be reverted',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        successAction: () => {
          this.removeInProgress = false;
          this.$bus.$emit('airport-handlers-list-reload');
        },
        cancelAction: () => {
          this.removeInProgress = false;
        },
        errorAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    reload () {
      this.loaded = false;
      this.loadAirportHandlers();
    }
  },
  beforeDestroy () {
    this.$bus.$off('airport-handlers-list-reload', this.reload);
  }
}
</script>

<style lang="scss" scoped>
.airport-handlers {
  margin: 30px 0;
  position: relative;

  &.is-loading {
    fieldset {
      min-height: 120px;
    }

    &:after {
      background: rgba(255, 255, 255, .66);
      content: "";
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }

    &:before {
      content: "Loading data...";
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translateX(-50%) translateY(-50%);
      z-index: 1;
    }
  }
}
</style>
